// §9.4 / §5.7 — putting an external app in a window.
//
// One function creates the iframe, one tears it down, and the §5.7 rule governs both: the frame is
// created **once**, into the `.win-content` slot that already exists, and the only thing that ever
// removes it is the window closing. Nothing here re-parents anything.
//
// The sandbox and permissions-policy attributes are §9.4's, verbatim, and `allow-same-origin` is
// among them on purpose — external apps need their own cookies and storage, which is exactly why
// the *origin* is the boundary and the sandbox is not.
import type { AppManifest } from '@hangar/shared';
import { APP_FRAME_ALLOW, APP_FRAME_SANDBOX } from './origin';
import type { BridgeConnection, BridgeHost } from './host';

export interface AppFrame {
  readonly element: HTMLIFrameElement;
  readonly connection: BridgeConnection;
  /** Remove the frame and close its bridge connection. Called only on window close (§5.7). */
  destroy(): void;
  /** §9.6 — reload the frame in place, for Retry and for a resumable rebuild. */
  reload(): void;
}

export interface MountFrameOptions {
  bridge: BridgeHost;
  manifest: AppManifest;
  instanceKey: string;
  /** The `.win-content` element the §5.7 host layer already created. */
  container: HTMLElement;
  welcome: Parameters<BridgeHost['connect']>[0]['welcome'];
  /** The shell's own origin, handed to the app so it knows who to talk to. */
  shellOrigin: string;
  /** Called on the frame's `load`, so §9.6 can start its handshake clock from the right moment. */
  onLoad?: () => void;
}

/** The `?shell=` parameter tells the app which origin to post to and believe (§9.3). */
export function entryUrlFor(manifest: AppManifest, shellOrigin: string): string {
  const url = new URL(manifest.entry);
  url.searchParams.set('shell', shellOrigin);
  return url.toString();
}

export function mountAppFrame(options: MountFrameOptions): AppFrame {
  const { bridge, manifest, container } = options;

  const connection = bridge.connect({
    instanceKey: options.instanceKey,
    appId: manifest.id,
    origin: manifest.origin,
    welcome: options.welcome,
  });

  const element = document.createElement('iframe');
  element.className = 'app-frame';
  element.dataset['appId'] = manifest.id;
  element.dataset['testid'] = `app-frame-${manifest.id}`;
  element.setAttribute('sandbox', APP_FRAME_SANDBOX);
  element.setAttribute('allow', APP_FRAME_ALLOW);
  element.setAttribute('title', manifest.name);
  element.addEventListener('load', () => {
    // `contentWindow` only exists once the document is there, and the host attributes inbound
    // messages by window identity — so this is the moment the connection becomes real.
    connection.setFrame(element.contentWindow);
    options.onLoad?.();
  });
  element.src = entryUrlFor(manifest, options.shellOrigin);
  container.appendChild(element);

  return {
    element,
    connection,
    destroy() {
      connection.setFrame(null);
      bridge.disconnect(options.instanceKey);
      element.remove();
    },
    reload() {
      // Re-setting `src` on the *same* element is a reload, not a re-parent — §5.7 is about the
      // element's identity, and a deliberate reload is the one case where a load is expected.
      element.src = entryUrlFor(manifest, options.shellOrigin);
    },
  };
}
